import { useBloc, useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import { Button } from "react-aria-components";
import { LoincCodingCode } from "src/constants/fhir";
import { IconEye, IconEyeOff } from "src/constants/icons";
import { APP_BREAKPOINT } from "src/constants/layout";
import translate from "src/lib/translate";
import UserPreferencesCubit, {
  chartVisibilityDependencies
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import Translate from "src/ui/components/Translate/Translate";
import DataHubCubit, { ChartUnits } from "./DataHubCubit";
import { renderTimeRangeContext } from "./DataTimeRangeButtons";

//#region Styled components
const Wrapper = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 0 0 1rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    margin: 0 0 1.25rem;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 0.25rem;

  p {
    margin: 0;
    font-weight: 500;
    font-size: 1.125rem;

    @media screen and (min-width: ${APP_BREAKPOINT}px) {
      font-size: 1.25rem;
    }
  }
`;

const TimeRange = styled.p`
  color: var(--color-charcoal);
`;

const Progress = styled.p`
  color: var(--color-charcoal-60);

  &[data-blurred="true"] {
    filter: blur(0.3rem);
    user-select: none;
  }
`;

const VisibilityToggle = styled(Button)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 0;
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-family);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: -0.02em;
  color: var(--color-charcoal-60);

  svg {
    display: block;
  }

  &::after {
    content: "";
    position: absolute;
    inset: -0.75rem -0.25rem;
  }

  &[data-blurred="true"] {
    color: var(--color-accessible-blue);
  }
`;
//#endregion

const DataStatus: FC = () => {
  const [
    { activeTimeRange, activeProgress, activeLoincCode, chartData },
    {
      formatValueDataStatus,
      formatBloodGlucoseProgress,
      activeChartBlurred,
      activeVisibilityPreference,
      toggleActiveChartVisibility
    }
  ] = useBloc(DataHubCubit, {
    // activeChartBlurred and activeVisibilityPreference are getters, so they are
    // not tracked by the state proxy — name their dependencies explicitly or the
    // blur never repaints on tab switch (same trap as DataTabs).
    dependencySelector: (s) => [
      s.activeLoincCode,
      s.activeProgress,
      s.chartData
    ]
  });
  // Subscribing to preferences is what repaints this row after the member
  // toggles visibility here or in Profile → Health Data. The dependency has to
  // be explicit: activeChartBlurred reads the preference through the cubit, and
  // useBloc only tracks fields read off the state proxy during render.
  useBlocNext(UserPreferencesCubit, {
    dependencySelector: chartVisibilityDependencies
  });

  const canToggleVisibility = activeVisibilityPreference !== undefined;

  const progress =
    activeProgress?.difference !== undefined && activeProgress.unit
      ? {
          difference: activeProgress.difference,
          unit: activeProgress.unit,
          secondaryDifference: activeProgress.secondaryDifference
        }
      : undefined;

  const showVisibilityToggle =
    canToggleVisibility && Boolean(chartData?.length);

  if (!progress && !showVisibilityToggle) {
    return null;
  }

  const renderSingleValue = (difference: number, unit: string) => {
    if (activeLoincCode === LoincCodingCode.bloodGlucoseCapillary) {
      return formatBloodGlucoseProgress(difference);
    }

    return (
      <>
        {formatValueDataStatus(difference, unit)}
        {unit}
        {unit === ChartUnits.steps && (
          <Translate msg="chart.average" variables={{ context: "perDay" }} />
        )}
      </>
    );
  };

  const renderProgress = (value: NonNullable<typeof progress>) => {
    const { difference, secondaryDifference, unit } = value;

    if (secondaryDifference !== undefined) {
      return (
        <>
          {formatValueDataStatus(difference, unit)}/
          {formatValueDataStatus(secondaryDifference, unit)}
          {unit}
        </>
      );
    }

    return renderSingleValue(difference, unit);
  };

  return (
    <Wrapper className="status">
      {progress && (
        <FlexContainer>
          <TimeRange>
            {translate("timeRange.long", {
              context: renderTimeRangeContext(activeTimeRange?.domain)
            })}
          </TimeRange>

          <Progress
            data-blurred={activeChartBlurred}
            aria-hidden={activeChartBlurred}
          >
            {renderProgress(progress)}
          </Progress>
        </FlexContainer>
      )}

      {showVisibilityToggle && (
        <VisibilityToggle
          data-blurred={activeChartBlurred}
          onPress={() => void toggleActiveChartVisibility()}
        >
          <Translate
            msg={
              activeChartBlurred
                ? "dataHub.visibility.show"
                : "dataHub.visibility.hide"
            }
          />
          {activeChartBlurred ? <IconEye /> : <IconEyeOff />}
        </VisibilityToggle>
      )}
    </Wrapper>
  );
};

export default DataStatus;
